import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useEmployees, useHrRefresh } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import type { HrEmployeeRow } from "../../lib/typesHr";
import { Modal } from "../ui/Modal";
import { Pill } from "../ui/Badges";

type Tone = "green" | "amber" | "red" | "purple" | "gray";

/** Runs an HR mutation: busy flag, error toast (translated by code), success toast, and a refresh of every HR query. */
export function useAction() {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const refresh = useHrRefresh();
  const [busy, setBusy] = useState(false);
  async function act<R>(fn: () => Promise<R>, successKey?: string): Promise<R | undefined> {
    setBusy(true);
    try {
      const r = await fn();
      if (successKey) toast(String(t(successKey)), "success");
      await refresh();
      return r;
    } catch (e) {
      toastError(e);
      return undefined;
    } finally {
      setBusy(false);
    }
  }
  return { act, busy };
}

export const useEmpName = () => {
  const { locale } = useLocale();
  return (e: { full_name: string; full_name_ar?: string | null } | null | undefined) => (e ? (locale === "ar" && e.full_name_ar ? e.full_name_ar : e.full_name) : "—");
};

const TONE: Record<string, Tone> = {
  active: "green", approved: "green", paid: "green", present: "green", settled: "green", closed: "gray", inside: "green", ok: "green",
  pending: "amber", requested: "amber", calculated: "purple", draft: "gray", late: "amber", expected: "gray", incomplete: "amber", on_notice: "amber", suspended: "amber", scheduled: "gray", deferred: "amber",
  rejected: "red", absent: "red", cancelled: "gray", withdrawn: "gray", void: "red", terminated: "red", written_off: "red", error: "red", reversed: "gray",
  leave: "purple", holiday: "purple", weekend: "gray", off: "gray", left: "gray", applied: "green", deducted: "green", calculating: "amber", open: "amber", candidate: "gray",
};
export function StatusPill({ status, ns = "status" }: { status: string; ns?: string }) {
  const { t } = useTranslation();
  return <Pill tone={TONE[status] ?? "gray"}>{t(`hr.${ns}.${status}`, { defaultValue: status })}</Pill>;
}

export const mins = (m: number | null | undefined) => {
  const v = Math.max(0, Math.round(m ?? 0));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[10px] font-medium text-gray-500">{hint}</span>}
    </label>
  );
}

export function EmployeeSelect({ value, onChange, status = "active", placeholder, id }: { value: string; onChange: (id: string) => void; status?: string; placeholder?: string; id?: string }) {
  const { t } = useTranslation();
  const emps = useEmployees({ status });
  const name = useEmpName();
  return (
    <select id={id} className="field" value={value} onChange={(e) => onChange(e.target.value)} aria-label={placeholder ?? t("hr.employee")}>
      <option value="">{placeholder ?? t("hr.chooseEmployee")}</option>
      {(emps.data ?? []).map((e: HrEmployeeRow) => <option key={e.id} value={e.id}>{e.employee_no} — {name(e)}</option>)}
    </select>
  );
}

/** Asks for the mandatory reason of a rejection / cancellation / void. */
export function ReasonDialog({ open, title, confirmLabel, onClose, onConfirm, danger = true }: { open: boolean; title: string; confirmLabel: string; onClose: () => void; onConfirm: (reason: string) => void; danger?: boolean }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const ok = reason.trim().length >= 3;
  return (
    <Modal open={open} title={title} onClose={onClose} size="sm" footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className={danger ? "btn-danger" : "btn-primary"} disabled={!ok} onClick={() => { onConfirm(reason.trim()); setReason(""); }}>{confirmLabel}</button></>}>
      <Field label={t("hr.reason")}><textarea className="field min-h-20" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
    </Modal>
  );
}

export function Kpi({ label, value, tone = "navy", hint }: { label: string; value: ReactNode; tone?: "navy" | "orange" | "purple" | "green" | "red"; hint?: string }) {
  const color = { navy: "text-cosmic-navy", orange: "text-solar-orange", purple: "text-cosmic-purple", green: "text-emerald-600", red: "text-red-600" }[tone];
  return (
    <div className="card p-4">
      <div className="text-[10px] font-black uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`num mt-1 font-mono text-xl font-black sm:text-2xl ${color}`}>{value}</div>
      {hint && <div className="mt-1 text-[10px] font-semibold text-gray-500">{hint}</div>}
    </div>
  );
}

export function Table({ head, children, empty }: { head: string[]; children: ReactNode; empty?: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-separate border-spacing-0 text-xs">
        <thead>
          <tr>{head.map((h, i) => <th key={i} className="border-b border-gray-200 px-2 py-2 text-start text-[10px] font-black uppercase tracking-wide text-gray-500">{h}</th>)}</tr>
        </thead>
        <tbody className="[&_td]:border-b [&_td]:border-gray-100 [&_td]:px-2 [&_td]:py-2 [&_td]:align-middle">{children}</tbody>
      </table>
      {empty}
    </div>
  );
}

/** "2026-09-01" → a localised short date without time-zone surprises. */
export const dateOnly = (d: string | null | undefined, locale: "ar" | "en") =>
  d ? new Intl.DateTimeFormat(locale === "ar" ? "ar-JO-u-nu-latn" : "en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${d.slice(0, 10)}T00:00:00Z`)) : "—";

export const todayIso = () => new Date().toISOString().slice(0, 10);
export const monthFirstIso = (offset = 0) => { const d = new Date(); d.setUTCMonth(d.getUTCMonth() + offset, 1); return d.toISOString().slice(0, 10); };
